import { useEffect, useState } from 'react';
import s from './Checkout.module.scss';
import AddressCard from './AddressCard';
import Promocode from './Promocode';
import Button from 'components/shared/Button';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useGetShippingAddress from 'sdk/hooks/shop/useGetShippingAddress';
import AddNewAddressForm from './AddNewAddressForm';
import useUpdateShippingAddressById from 'sdk/hooks/useUpdateShippingAddressById';
import useAddShippingAddress from 'sdk/hooks/useAddShippingAddress';
import { createInvoice } from 'sdk/hooks/payments/useCreateInvoice';
import { useGetMyUser } from 'sdk/hooks/user/useGetMyUser';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as RiyalIcon } from 'images/new-icons/riyal.svg';

const subtotals = (title, price) => {
  return (
    <div className={s.checkout_subtotal_item}>
      <div className={s.checkout_subtotal_title}>{title}</div>
      <div className={s.checkout_subtotal_price} style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
        <RiyalIcon style={{ width: '14px', height: '14px' }} />
        {price}
      </div>
    </div>
  );
};

const Checkout = ({ data, cartItems, setStep }) => {
  const navigate = useNavigate();
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { mutate: updateShippingAddress } = useUpdateShippingAddressById();
  const { mutate: addShippingAddress } = useAddShippingAddress();

  const { data: shippingAddresses, refetch: refetchShippingAddresses } =
    useGetShippingAddress(userId);
  const { data: userData } = useGetMyUser(userId || '');
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);

  const [editingShippingAddressId, setEditingShippingAddressId] = useState(null);

  const [isAddShippingAddress, setIsAddShippingAddress] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [cartItemsWithDiscount, setCartItemsWithDiscount] = useState(
    cartItems?.filter(item => !item.gift_card_id)
  );

  const subtotalPrice = cartItemsWithDiscount?.reduce((acc, item) => {
    return acc + (item.discountedPrice ?? item.price) * item.quantity;
  }, 0);

  const discountPrice = cartItemsWithDiscount?.reduce((acc, item) => {
    const discount = item.discountedPrice ? (item.price - item.discountedPrice) * item.quantity : 0;
    return acc + discount;
  }, 0);

  const taxPrice = 0;
  const shippingPrice = 0;
  const totalPrice = subtotalPrice + taxPrice + shippingPrice;

  const handleSelectShippingAddress = id => {
    setSelectedShippingAddress(id);
  };

  const handleBuy = async () => {
    setIsLoading(true);
    try {
      const response = await createInvoice({
        order: cartItemsWithDiscount,
        totalPrice: totalPrice * 100,
        email: userData?.email,
        fullName: userData?.username,
        phoneNumber: '+' + userData?.phone,
        description: 'Test Description',
        productIds: data?.map(item => item?.product?.id),
        user_id: userId,
        shipping_address_id: selectedShippingAddress || shippingAddresses?.[0]?.id,
        salonId: data?.[0]?.product?.salon_id,
      });

      const res = response;

      if (!res || typeof res !== 'string') {
        console.error('  Invalid invoice URL:', res);
        throw new Error('Failed to create invoice - no valid link in response');
      }

      window.location.href = res;
    } catch (err) {
      console.error(' Error creating invoice:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCartItemsWithDiscount(cartItems?.filter(item => !item.gift_card_id));
  }, [cartItems]);

  return (
    <div
      className={s.checkout}
      style={{
        height: isAddShippingAddress || (shippingAddresses?.length ?? 0) >= 2 ? 'auto' : '100%',
      }}
    >
      <div>
        <div className={s.address_card_top}>
          <div className={s.address_card_top_left}>Shipping To</div>
          {shippingAddresses ? (
            shippingAddresses?.length >= 1 ? (
              <></>
            ) : (
              <div
                className={s.address_card_top_right}
                onClick={() => setIsAddShippingAddress(!isAddShippingAddress)}
              >
                + Add Address
              </div>
            )
          ) : (
            <></>
          )}
        </div>
        <div className={s.addresses}>
          {shippingAddresses?.map(item => {
            const address = `${item?.country}. ${item?.city}, ${item?.state}, ${item?.street} ${item?.apartment}, ${item?.zip_code}`;
            return (
              <AddressCard
                address={address}
                number={item?.user?.phone}
                isSelected={selectedShippingAddress === item?.id}
                isEditing={editingShippingAddressId === item?.id}
                onSelect={() => handleSelectShippingAddress(item?.id)}
                onEdit={setEditingShippingAddressId}
                item={item}
                updateAddress={updateShippingAddress}
                refetch={refetchShippingAddresses}
              />
            );
          })}
        </div>
        {isAddShippingAddress && (
          <AddNewAddressForm
            refetch={refetchShippingAddresses}
            addAddress={addShippingAddress}
            hide={setIsAddShippingAddress}
          />
        )}
      </div>

      <Promocode
        cartItems={cartItems?.filter(item => !item.gift_card_id)}
        onApplyDiscount={setCartItemsWithDiscount}
      />
      <div className={s.checkout_subtotal}>
        {subtotals('Subtotal', subtotalPrice)}
        {subtotals('Tax', taxPrice)}
        {subtotals('Shipping', shippingPrice)}
        {subtotals('Discount', discountPrice)}
      </div>
      <div className={s.checkout_total}>
        <div className={s.checkout_total_title}>
          <div>Total</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <RiyalIcon style={{ width: '14px', height: '14px' }} />
            {totalPrice}
          </div>
        </div>
      </div>
      <Button
        wide
        darkGreen
        onClick={handleBuy}
        loading={isLoading}
        disabled={!userData?.email || !selectedShippingAddress || isLoading}
        style={{ marginTop: 'auto' }}
      >
        Buy now {/* Buy now  BUYING ITEMS IN CART */}
      </Button>
    </div>
  );
};

export default Checkout;
